import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import Api, { HealthCenter, User } from '@silbo/core';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { authenticationErrors } from '../../const/authentication-errors';
import { useMicrosoftAuth } from '../../features/bed-management/hooks/useMicrosoftAuth';
import { PORTER_PASSWORD_LENGTH } from '../../features/patient-transport/const/porter-password-length';
import { retrieveAndStoreFcmToken } from '../../notifications/notification';
import { basePath } from '../../utils/base-path';
import { BandwidthStatusEnum, useBandwidthContext } from '../BandwidthContext';
import { AuthenticationContext } from './AuthenticationContext';

const MIN_VALIDATION_INTERVAL = 60_000;

export const AuthenticationProvider: FC<PropsWithChildren> = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | undefined>(Api.User.current() as User);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!user);
  const [healthCenter, setHealthCenter] = useState<HealthCenter | undefined>(user?.healthCenters?.[0]);
  const [loginError, setLoginError] = useState<string>('');
  const [microsoftError, setMicrosoftError] = useState<string>('');
  const { t } = useTranslation();
  const { status } = useBandwidthContext();
  const { login: msLogin, logout: msLogout, handleRedirectResult, isLoading: isMsLoading } = useMicrosoftAuth();

  const lastValidationTimeRef = useRef<number>(0);
  const isValidatingRef = useRef(false);
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasHandledRedirectRef = useRef(false);

  // Attach the user to Datadog logs/RUM on every session start, including sessions
  // restored from localStorage that never go through a login flow. Re-runs when the
  // health center name resolves so `usr.healthCenter` is filled once its data is fetched.
  const userHealthCenterName = user?.healthCenters?.[0]?.name;
  useEffect(() => {
    if (!user) {
      return;
    }
    const datadogUser = {
      id: user.id,
      name: user.username,
      healthCenter: userHealthCenterName,
      role: user.role,
    };
    datadogRum.setUser(datadogUser);
    datadogLogs.setUser(datadogUser);
  }, [user, userHealthCenterName]);

  const logout = useCallback(async () => {
    const username = user?.username;
    // Clear app state first to prevent UI flashing
    flushSync(() => {
      setUser(undefined);
      setIsAuthenticated(false);
      setHealthCenter(undefined);
    });
    const loginPath = `${basePath}login`;

    if (status === BandwidthStatusEnum.Online) {
      try {
        await Api.User.logOut();
        await msLogout(username);

        // Use window.location for reliable redirection since router context isn't available here
        const currentPath = window.location.pathname;

        if (!currentPath.includes('/login')) {
          window.location.href = loginPath;
        }
      } catch {
        window.location.href = loginPath;
      }
    }
    datadogRum.clearUser();
    datadogLogs.clearUser();
  }, [status, user?.username, msLogout]);

  const validateCurrentSession = useCallback(
    async (forceValidation = false) => {
      if (isValidatingRef.current) {
        return;
      }

      const now = Date.now();
      if (!forceValidation && now - lastValidationTimeRef.current < MIN_VALIDATION_INTERVAL) {
        return;
      }

      lastValidationTimeRef.current = now;
      isValidatingRef.current = true;

      if (status === BandwidthStatusEnum.Online) {
        try {
          // First check if there's an active Parse session
          const hasSession = await Api.Session.current();

          if (!hasSession) {
            await logout();
            return;
          }

          // If we have a session but no user, or user is not authenticated, logout
          if (!user || !isAuthenticated) {
            datadogLogs.logger.warn('Session validation failed - missing user or not authenticated', {
              hasUser: !!user,
              isAuthenticated,
              sessionExists: !!hasSession,
            });
            await logout();
            return;
          }

          // Validate that the user still exists in the database
          await new Api.Query(User).get(user.id);
        } catch (e) {
          // If no status code is provided, that's an network error
          if (e.statusCode) {
            await logout();
          }
        } finally {
          isValidatingRef.current = false;
        }
      } else {
        isValidatingRef.current = false;
      }
    },
    [logout, isAuthenticated, user, status]
  );

  const storeParseUser = useCallback(async (currentUser: Parse.User) => {
    if (!currentUser.id) {
      throw new Error('User ID not found');
    }
    const user = await new Api.Query(User).include('healthCenters').get(currentUser.id);

    if (user.role !== 'BED_MANAGER') {
      await Api.User.logOut();
      throw new Error('Unauthorized role');
    }

    datadogLogs.logger.debug('Login successful (BedManager), retrieving FCM token', { userId: user.id });
    retrieveAndStoreFcmToken();

    flushSync(() => {
      setUser(user);
      setHealthCenter(user.healthCenters?.[0]);
      setIsAuthenticated(true);
      setLoginError('');
      setMicrosoftError('');
    });
    datadogLogs.logger.debug('Login flow completed', {
      userId: user.id,
      isAuthenticated: true,
      role: 'BED_MANAGER',
    });
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      setLoading(true);
      setLoginError('');

      try {
        const currentUser = await Api.User.logIn(username, password);
        await storeParseUser(currentUser);
      } catch (err) {
        const error = err as { message: string };
        const errorKey =
          error.message && authenticationErrors[error.message]
            ? authenticationErrors[error.message]
            : 'errors.anErrorOccurred';
        // @ts-expect-error - errorKey might not be in translations
        setLoginError(t(errorKey));
      } finally {
        setLoading(false);
      }
    },
    [t, storeParseUser]
  );

  const handleMicrosoftError = useCallback(
    (err: unknown) => {
      const error = err as { message?: string };
      if (error.message === 'USER_DISABLED') {
        setMicrosoftError(t('auth.userDisabled'));
      } else if (error.message === 'NO_MICROSOFT_ACCOUNT_LINKED') {
        setMicrosoftError(t('auth.noMicrosoftAccountLinked', { ns: 'bed-management' }));
      } else {
        datadogLogs.logger.error('Microsoft login failed', { error });
        setMicrosoftError(t('errors.anErrorOccurred'));
      }
    },
    [t]
  );

  // Starts the Microsoft redirect flow. This navigates away to Microsoft, which redirects back
  // to the login page where the completion effect below takes over.
  const microsoftLogin = useCallback(async () => {
    setLoading(true);
    setMicrosoftError('');

    try {
      await msLogin();
    } catch (err) {
      setLoading(false);
      handleMicrosoftError(err);
    }
  }, [msLogin, handleMicrosoftError]);

  // Completes the Microsoft redirect once the app lands back on the login page. The redirect
  // URI is that same page, so MSAL processes the response here in a single load (no detour).
  // `handleRedirectResult` returns the linked user only when actually returning from a
  // redirect, so on regular loads this is a cheap no-op. The ref guards against React
  // StrictMode double-invocation.
  useEffect(() => {
    if (hasHandledRedirectRef.current) {
      return;
    }
    hasHandledRedirectRef.current = true;

    const completeMicrosoftLogin = async () => {
      try {
        const parseUser = await handleRedirectResult();
        if (parseUser) {
          await storeParseUser(parseUser);
        }
      } catch (err) {
        handleMicrosoftError(err);
      }
    };

    completeMicrosoftLogin();
  }, [handleRedirectResult, storeParseUser, handleMicrosoftError]);

  const loginPorter = useCallback(
    async (username: string, password: string) => {
      if (password.length !== PORTER_PASSWORD_LENGTH) {
        return;
      }

      setLoading(true);
      setLoginError('');

      try {
        const currentUser = await Api.User.logIn(username, password);
        if (!currentUser.id) {
          throw new Error('User ID not found');
        }
        const user = await new Api.Query(User).include('healthCenters').get(currentUser.id);

        // Vérifier le rôle pour Porter
        if (user.role !== 'PORTER') {
          await Api.User.logOut();
          setLoading(false);
          throw new Error('Unauthorized role');
        }

        datadogLogs.logger.debug('Login successful (Porter), retrieving FCM token', { userId: user.id });
        await retrieveAndStoreFcmToken();

        flushSync(() => {
          setUser(user);
          setHealthCenter(user.healthCenters?.[0]);
          setIsAuthenticated(true);
        });
        datadogLogs.logger.debug('Login flow completed', { userId: user.id, isAuthenticated: true, role: 'PORTER' });
      } catch (err) {
        const error = err as { message: string };
        const errorKey =
          error.message && authenticationErrors[error.message]
            ? authenticationErrors[error.message]
            : 'errors.unknownAuthError';
        // @ts-expect-error - errorKey might not be in translations
        setLoginError(t(errorKey));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const resetLoginError = useCallback(() => setLoginError(''), []);

  // Fetch health centers if needed (for both modes)
  useEffect(() => {
    const fetchHealthCenters = async () => {
      if (user?.healthCenters?.some((hc) => !hc.isDataAvailable())) {
        setLoading(true);
        try {
          await Promise.all(user.healthCenters.filter((hc) => !hc.isDataAvailable()).map((hc) => hc.fetch()));
          setHealthCenter(user.healthCenters?.[0]);
        } catch (error) {
          datadogLogs.logger.error('Error fetching user health centers', { error });
        } finally {
          setLoading(false);
        }
      }
    };

    if (user) {
      fetchHealthCenters();
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated) {
      validateCurrentSession();
    }
  }, [isAuthenticated, validateCurrentSession]);

  useEffect(() => {
    if (isAuthenticated) {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }

      sessionCheckIntervalRef.current = setInterval(() => {
        validateCurrentSession();
      }, MIN_VALIDATION_INTERVAL);

      return () => {
        if (sessionCheckIntervalRef.current) {
          clearInterval(sessionCheckIntervalRef.current);
          sessionCheckIntervalRef.current = null;
        }
      };
    } else {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
    }
  }, [isAuthenticated, validateCurrentSession]);

  useEffect(() => {
    const handleActivity = () => {
      if (isAuthenticated && !isValidatingRef.current) {
        validateCurrentSession(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated && !isValidatingRef.current) {
        validateCurrentSession(true);
      }
    };

    window.addEventListener('focus', handleActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, validateCurrentSession]);

  return (
    <AuthenticationContext.Provider
      value={{
        user,
        loginError,
        microsoftError,
        isAuthenticated,
        healthCenter,
        loading: loading || isMsLoading,
        login,
        microsoftLogin,
        loginPorter,
        logout,
        resetLoginError,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};
